<template>
    <div class="calendar-overview-root">
        <Header :label="$t('calendar.header')" overlap="small" />
        <UserNavSection />

        <MainLayout>
            <ProfileModule slot="right" />
            <FeaturedResource slot="right" />
            <VerifyModule slot="right" />
            <div v-if="profileLoading" slot="center" class="w-100">
                <SectionHeader slot="center" style="position:relative">
                    <Breadcrumb slot="left" :trace="breadcrumb"
                /></SectionHeader>
                <Spinner class="loading-spinner mt-5" />
            </div>
            <div v-else-if="!hasCompletedProfile" slot="center">
                <SectionHeader style="position:relative">
                    <Breadcrumb slot="left" :trace="breadcrumbBlocked"
                /></SectionHeader>
                <div class="w-100 d-flex flex-column align-items-center pb-5">
                    <img
                        :src="profileSvg"
                        style="display:block;margin:auto;width:200px;height:200px;object-fit:scale-down;margin-top:40px;"
                    />
                    <h6 class="text-center mb-4">
                        {{ $t('calendar.completedYourProfile') }}
                    </h6>
                    <BaseButton variant="primary" @click="toProfile">
                        {{ $t('calendar.toMyProfile') }}
                    </BaseButton>
                </div>
            </div>
            <div v-else slot="center">
                <SectionHeader slot="center" style="position:relative">
                    <Breadcrumb slot="left" :trace="breadcrumb"
                /></SectionHeader>
                <NoContent
                    v-if="appointmentMissing"
                    slot="center"
                    :caption="$t('calendar.appointmentNotActive')"
                >
                </NoContent>
                <NoContent
                    v-if="notAvailable"
                    slot="center"
                    :caption="$t('calendar.appointmentNotHavingAvailability')"
                >
                </NoContent>
                <div v-else slot="center">
                    <Paper tall class="calendar-mycalendar-wrapper">
                        <div v-if="!loaded">
                            <AvailabilityViewerSkeleton />
                        </div>
                        <div class="cal-availability-wrapper">
                            <div id="cronofy-viewer"></div>
                        </div>
                    </Paper>
                </div>
                <div slot="center">
                    <BaseButton
                        id="backButton"
                        class="w-100"
                        style="margin-top: 15px;"
                        variant="primary"
                        @click="handleBackClick"
                    >
                        {{ $t('calendar.backToBookingBtn') }}
                    </BaseButton>
                </div>
            </div>
        </MainLayout>
    </div>
</template>

<script>
// Components
import Header from '~/components/layouts/Header';
import MainLayout from '~/components/layouts/mainlayout/MainLayout';
import UserNavSection from '~/shared/UserNavSection';
import ProfileModule from '~/components/smart/ProfileModule/ProfileModule';
import VerifyModule from '~/components/miscellaneous/VerifyModule';
import FeaturedResource from '~/components/smart/FeaturedResource';
import NoContent from '~/components/miscellaneous/NoContent';

import Paper from '~/components/builders/Paper';

import * as CronofyElements from 'cronofy-elements';

// Services
import DialogService from '~/services/DialogService';
import AvailabilityViewerSkeleton from './components/AvailabilityViewerSkeleton';
// Assets

import EmptySvg from '~/assets/img/empty.svg';
import SectionHeader from '~/components/layouts/SectionHeader';
import Breadcrumb from '../../../shared/Breadcrumb';

import URLS from '~/URLS';
import i18n from '~/i18n';
import moment from 'moment';
import UniversityService from '~/services/UniversityService';
import { maxPossibleScore, getCurrentScore } from '~/utils/mappers/mapProfileToActions';
import ProfileSvg from '~/assets/img/profile.svg';

const EXTRA_DAYS_TO_BOOK_LIMIT = 14;

export default {
    name: 'AppointmentBook',
    components: {
        Breadcrumb,
        Paper,
        Header,
        UserNavSection,
        MainLayout,
        ProfileModule,
        VerifyModule,
        FeaturedResource,
        SectionHeader,
        AvailabilityViewerSkeleton,
        NoContent,
    },
    data: () => ({
        emptyImage: EmptySvg,
        profileSvg: ProfileSvg,
        breadcrumb: [
            // {
            //     exact: true,
            //     text: i18n.t('calendar.myCalendar'),
            //     to: URLS.calendar,
            // },
            {
                exact: true,
                text: i18n.t('calendar.bookAnAppointment'),
                to: URLS.calendar.concat(URLS.book),
            },
            {
                exact: true,
                text: i18n.t('calendar.loadingAppointment'),
                to: URLS.calendar.concat(URLS.book),
            },
        ],
        availability: {
            elementToken: null,
            members: [],
        },
        loaded: false,
        type: {
            title: '',
            duration: 30,
            location: 'N/A',
        },
        appointmentMissing: false,
        notAvailable: false,
    }),
    computed: {
        hasCompletedProfile() {
            const userData = this.userData() || {};
            const profileData = this.candidateData() || {
                languages: [],
                degrees: [],
            };
            return getCurrentScore(userData, profileData) >= maxPossibleScore;
        },
        profileLoading() {
            return this.userLoading() || this.candidateLoading();
        },
        user() {
            return this.$interfaces.user.me.get({
                reactive: true,
            });
        },
        candidate() {
            return this.$interfaces.candidate.me.get({
                reactive: true,
            });
        },
        breadcrumbBlocked() {
            let crumbs = this.breadcrumb;
            crumbs.pop();
            crumbs.push({
                exact: true,
                text: i18n.t('calendar.needToCompleteProfile'),
                to: URLS.calendar.concat(URLS.book),
            });
            return crumbs;
        },
    },
    methods: {
        handleBackClick() {
            this.$router.push(this.$URLS.calendar.concat(this.$URLS.book));
        },
        userLoading() {
            return this.user.loading;
        },
        candidateLoading() {
            return this.candidate.loading;
        },
        userData() {
            return this.user.data;
        },
        candidateData() {
            return this.candidate.data;
        },
        toProfile() {
            this.$router.push(this.$URLS.spotlight);
        },
        getLocalTimezone() {
            return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Etc/UTC';
        },
        handleBookClick(notification) {
            DialogService.openAppointmentBookingDialog(notification, {
                location: this.type.location,
                title: this.type.title,
            });
        },
        getAvailablePeriods() {
            if (this.type && this.type.start && this.type.end) {
                let nowDate = new Date();
                let now = nowDate.toISOString();

                let res = {
                    start: this.type.start,
                    end: this.type.end,
                };
                let startDate = new Date(this.type.start);
                if (nowDate > startDate) {
                    res.start = now;
                }
                return [res];
            }

            //When using startOf('day'):
            //We need to go through UTC and a given offset to handle potential differences between browser and system languages.
            //Primarily for handling debug purposes with time zone spoofing in browser, but it's also more robust.
            //Also we want to pass all data to Cronofy in utc to keep things robust.
            let offset = new Date().getTimezoneOffset();

            //Do not allow talent to book an event the same day they are viewing.
            //Possible extension: Let career services specify "notice hours" which controls this.
            //Then we would change it to .add(noticeTime, 'hours')
            //And remember to let the final start time be the latest of the time specified by career service and
            //the time derived from now + noticeTime, or the next day
            //The appointment rule will be a final mask over available times anyways, so no need to worry about this.

            let nextDayStart = moment()
                .add(1, 'days')
                .utc()
                .startOf('day')
                .add(offset, 'minutes');
            //let ealiestBookToday = moment().add(noticeTime + 1, 'hours').startOf('hour').utc(); //Rounds off to the following hour given by noticeTime
            let firstPossibleDate = moment()
                .subtract(42, 'years')
                .utc(); //prototype: the first possible date, specified by career services

            //Current hack, just add a specified number of days. Would want career services to be able to specify this.
            let endDay = moment(nextDayStart).add(EXTRA_DAYS_TO_BOOK_LIMIT, 'days'); //Remember to clone, not just copy pointer..

            let firstPossibleBook = moment.max(nextDayStart, firstPossibleDate);
            let latestPossibleBook = moment.max(endDay); //Add potential others?
            return [
                {
                    start: firstPossibleBook.format(),
                    end: latestPossibleBook.format(),
                },
            ]; //Cronofy wants an array.
        },
    },
    async mounted() {
        let schoolData = await UniversityService.getCurrentSchoolData();

        if (schoolData.id) {
            let type = await this.$interfaces.calendar.type.get({
                school_id: schoolData.id,
                only_active: true,
            });
            this.type = type.data.results.filter(item => item.id === this.$route.params.id)[0];
            if (!this.type) {
                this.breadcrumb.pop();
                this.breadcrumb.push({
                    exact: true,
                    text: i18n.t('calendar.appointmentMissing'),
                    to: URLS.calendar
                        .concat(URLS.book)
                        .concat('/')
                        .concat(this.$route.params.id),
                });
                this.appointmentMissing = true;
                this.loaded = true;
                return;
            }

            if (this.type && this.type.start && this.type.end) {
                let now = new Date();
                let start = new Date(this.type.start);
                let end = new Date(this.type.end);
                if (start < now && end < now) {
                    this.breadcrumb.pop();
                    this.breadcrumb.push({
                        exact: true,
                        text: i18n.t('calendar.appointmentMissing'),
                        to: URLS.calendar
                            .concat(URLS.book)
                            .concat('/')
                            .concat(this.$route.params.id),
                    });
                    this.appointmentMissing = true;
                    this.loaded = true;
                    return;
                }
            }
            let availability = await this.$interfaces.calendar.availability
                .get({
                    appointment_type_id: this.$route.params.id,
                    school_id: schoolData.id,
                    forceUpdate: true,
                })
                .catch(e => {
                    this.breadcrumb.pop();
                    this.breadcrumb.push({
                        exact: true,
                        text: i18n.t('calendar.appointmentMissingRules'),
                        to: URLS.calendar
                            .concat(URLS.book)
                            .concat('/')
                            .concat(this.$route.params.id),
                    });
                    this.notAvailable = true;
                    this.loaded = true;
                });
            if (!availability) {
                this.breadcrumb.pop();
                this.breadcrumb.push({
                    exact: true,
                    text: i18n.t('calendar.appointmentMissingRules'),
                    to: URLS.calendar
                        .concat(URLS.book)
                        .concat('/')
                        .concat(this.$route.params.id),
                });
                this.notAvailable = true;
                this.loaded = true;
                return;
            }
            this.availability.elementToken = availability.data.element_token;
            this.availability.members = availability.data.members.map(member => {
                return {
                    sub: member.sub,
                    availability_rule_ids: member.rule_id,
                    managed_availability: true,
                };
            });
            this.loaded = true;
        }
        this.$nextTick(() => {
            CronofyElements.AvailabilityViewer({
                target_id: 'cronofy-viewer',
                element_token: this.availability.elementToken,
                config: {
                    start_time: '08:00',
                    end_time: '20:00',
                    duration: this.type.duration,
                    interval: this.type.duration,
                },
                locale: 'en',
                availability_query: {
                    participants: [
                        {
                            required: 1,
                            members: this.availability.members,
                        },
                    ],
                    required_duration: { minutes: this.type.duration },
                    available_periods: this.getAvailablePeriods(),
                },
                callback: notification => this.handleBookClick(notification),
            });
        });
        this.breadcrumb.pop();
        this.breadcrumb.push({
            exact: true,
            text: this.type.title,
            to: URLS.calendar
                .concat(URLS.book)
                .concat('/')
                .concat(this.$route.params.id),
        });
    },
};
</script>

<style scoped>
.cal-availability-wrapper {
    padding: 50px;
}
@media only screen and (max-width: 600px) {
    .cal-availability-wrapper {
        padding-left: 0;
        padding-right: 0;
    }
}
</style>
